import React from "react";
import { DateTileProps, DateStatus } from "../types";
import { parseDateString } from "../utils/dateUtils";

const getStatusBadge = (status: DateStatus): string => {
  switch (status) {
    case "bingo":
      return "🏆";
    case "streak":
      return "🔥";
    case "hasNotes":
      return "📝";
    case "hasReminders":
      return "⏰";
    case "hasTodos":
      return "🎯";
    case "hasTrackers":
      return "📈";
    case "missed":
      return "";
    case "partial":
      return "⭐";
    case "todosCompleted":
      return "✅";
    default:
      return "";
  }
};

// Priority order for badge display
const getPrimaryBadge = (statuses: DateStatus[]): string => {
  const priority: DateStatus[] = [
    "bingo",
    "streak",
    "todosCompleted",
    "partial",
    "hasTodos",
    "hasReminders",
    "hasNotes",
    "hasTrackers",
  ];
  for (const s of priority) {
    if (statuses.includes(s)) return getStatusBadge(s);
  }
  return "";
};

const DateTile: React.FC<DateTileProps> = ({ data, onClick }) => {
  const date = parseDateString(data.date);
  const dayNumber = date.getDate();

  const totalTodos = data.todosCount + data.completedTodosCount;
  const totalItems = data.habitsCount + totalTodos;
  const completedItems = data.completedHabitsCount + data.completedTodosCount;
  const progressPercent =
    totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const derivedBingo =
    totalItems > 0 &&
    data.completedHabitsCount === data.habitsCount &&
    data.completedTodosCount === totalTodos;

  const isHot = (data.isBingo || derivedBingo) && data.isCurrentMonth;
  const hasStreak =
    (data.statuses.includes("streak") ||
      data.statuses.includes("partial") ||
      (completedItems > 0 && !isHot)) &&
    !isHot;

  let cellClasses =
    "day-cell relative rounded-[9px] border flex flex-col overflow-hidden cursor-pointer transition-all duration-150";
  let dateNumClasses = "text-[14px] lg:text-[16px] font-medium leading-none";

  if (!data.isCurrentMonth) {
    cellClasses += " opacity-20 pointer-events-none border-transparent";
  } else if (data.isToday) {
    cellClasses += " bg-[#3b5cde]/15 border-[#3b5cde] border-[1.5px]";
    dateNumClasses += " text-[#7b9fff]";
  } else if (isHot) {
    cellClasses += " bg-[#e87b3f]/15 border-[#e87b3f]/30";
    dateNumClasses += " dark:text-[#d4a058] text-[#a06820]";
  } else if (hasStreak) {
    cellClasses += " bg-[#f5a623]/10 border-[#f5a623]/20";
    dateNumClasses += " dark:text-[#d4a058] text-[#a06820]";
  } else {
    cellClasses +=
      " dark:bg-white/[0.04] bg-white dark:border-white/[0.06] border-black/[0.07]";
    dateNumClasses += " dark:text-white/30 text-gray-400";
  }

  const badge = data.isCurrentMonth
    ? isHot
      ? getStatusBadge("bingo")
      : getPrimaryBadge(data.statuses)
    : "";

  const handleClick = () => {
    if (data.isCurrentMonth) onClick(data.date);
  };

  return (
    <div
      className={cellClasses}
      style={{ aspectRatio: "1 / 1.2" }}
      onClick={handleClick}
      role="button"
      tabIndex={data.isCurrentMonth ? 0 : -1}
      aria-label={`${dayNumber}${data.isToday ? " (Today)" : ""}`}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && data.isCurrentMonth) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="p-1.5 lg:p-2 flex flex-col h-full">
        <span className={dateNumClasses}>{dayNumber}</span>
        {data.isCurrentMonth && totalItems > 0 && !isHot && (
          <span className="mt-1 text-[10px] lg:text-[13px] text-gray-400 dark:text-white/35 leading-none">
            {completedItems}/{totalItems}
          </span>
        )}
        {badge && (
          <span className="mt-auto text-[14px] lg:text-[15px] leading-none">
            {badge}
          </span>
        )}
      </div>

      {data.isCurrentMonth && (
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/[0.08] dark:bg-white/[0.08]">
          <div
            className={`h-full transition-all duration-200 ${
              isHot
                ? "bg-gradient-to-r from-[#f5a623] to-[#e87b3f]"
                : "bg-[#f5a623]/75 dark:bg-[#f5a623]/90"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default DateTile;
